/**
 * HeadlightQR — the one provider apps, panels, views and form inputs use for QR codes.
 *
 * Decodes and encodes payloads, mints codes in the customer's format, resolves a scan to a record
 * (including deleted ones), renders codes and labels, prints, and owns the scanning modes:
 *
 * - GoTo: the default; a scan shows what was scanned without navigating anywhere by itself.
 * - Pick: a field or picker asked for a code; only its entity types are accepted.
 * - Session: a job such as Receive or Put away; action cards are only acted on here.
 *
 * Handlers form a stack: the most recent handler sees a scan first and may decline it. GoTo handlers always sit
 * below Pick and Session ones, whatever order they were added in.
 */

const libPictProvider = require('pict-provider');
const libHeadlightQRCode = require('headlight-qrcode');

const libMatrix = require('../rendering/QR-Matrix.js');
const libPresets = require('../labels/Label-Media-Presets.js');
const libLabels = require('../labels/Label-Renderer.js');
const libPrint = require('../printing/Print-Transport.js');
const WedgeScanSource = require('../scanning/Wedge-Scan-Source.js');
const libScanGeolocation = require('../scanning/Scan-Geolocation.js');

const PRESET_STORAGE_KEY = 'HeadlightQR-MediaPreset';
const SCAN_LOG_LIMIT = 200;

/**
 * @typedef {Object} QRResolution
 * @property {'Found'|'Deleted'|'NotFound'|'Rejected'|'Action'|'Empty'|'Unresolved'} Status
 * @property {import('headlight-qrcode/source/Headlight-QRCode-Codec.js').Decoded} Decoded
 * @property {string} [Entity]
 * @property {Record<string, any>} [Record]
 * @property {number} [DuplicateCount] - Other live records sharing the code (reported to operations, not shown to users).
 * @property {string} [DeletedByName]
 * @property {string} [Reason]
 */

/**
 * @typedef {Object} ScanHandler
 * @property {string} Hash
 * @property {'GoTo'|'Pick'|'Session'} Mode
 * @property {string} [Label] - Shown in the mode bar, e.g. "Receiving at D02 Lab".
 * @property {Array<string>} [Accept] - Entity types this handler takes.
 * @property {Array<string>} [RawEntities] - How to read an unwrapped code while this handler is active.
 * @property {string} [Workflow] - Action-card workflow this handler owns, e.g. `cart`.
 * @property {Array<string>} [Workflows] - Several workflows, for a session that answers more than one set of cards.
 * @property {(pResolution: QRResolution, pScan: Partial<ScanEvent>) => (boolean|void|Promise<boolean|void>)} onScan - Return false to let the next handler try.
 *   The scan is recorded (and its tone played) once a handler takes it, so start slow work without awaiting it.
 */

/**
 * @typedef {Object} ScanEvent
 * @property {string} ID
 * @property {string} Time - ISO timestamp.
 * @property {'wedge'|'camera'|'manual'|'native'} Source
 * @property {string} Raw
 * @property {string} Mode
 * @property {string} [Handler]
 * @property {string} [Status]
 * @property {QRResolution} [Resolution]
 * @property {{ Latitude: number, Longitude: number, AccuracyMeters: number, FixTime: string }|null} [Geolocation]
 */

const _DefaultConfiguration =
{
	ProviderIdentifier: 'HeadlightQR',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,

	Environment: null,
	Configuration: {},
	CustomProperties: {},
	Entities: {},
	DefaultRawEntities: [ 'Sample' ],
	ErrorCorrection: 'Q',
	MinModuleMM: 0.5,
	DefaultMediaPreset: 'brother-62x100',
	Wedge: { Enabled: false },
	CaptureGeolocation: false,
	Feedback: { Sound: true, Vibrate: true },
};

class HeadlightQRProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		const tmpOptions = Object.assign({}, JSON.parse(JSON.stringify(_DefaultConfiguration)), pOptions);
		super(pFable, tmpOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		const tmpEnvironment = this.options.Environment || ((typeof window !== 'undefined' && window.location) ? libHeadlightQRCode.Hosts.getEnvironmentForPage({ Protocol: window.location.protocol, Hostname: window.location.hostname, ServerURL: window._OfflineServerBaseURL }) : 'Production');
		this.core = new libHeadlightQRCode(this.fable, { Entities: this.options.Entities, Configuration: this.options.Configuration, CustomProperties: this.options.CustomProperties, Environment: tmpEnvironment });

		/** @type {any} Entity source: `{ find(request), getUserName?(id) }`. */
		this.entitySource = this.options.EntitySource || null;
		/** @type {Array<ScanHandler>} */
		this.handlers = [];
		/** @type {Record<string, (pDecoded: any) => void>} workflow entry points, keyed `workflow/verb` */
		this.actionEntries = {};
		/** @type {Array<ScanEvent>} */
		this.scanLog = [];
		/** @type {Array<(pType: string, pDetail: any) => void>} */
		this.listeners = [];
		this.lastGeolocation = null;
		this.scanCounter = 0;
		this.wedge = null;
		this.pendingScanOnce = null;
		/** @type {string|null} the customer's default label stock (QRCodes/Printing configuration) */
		this.customerMediaPresetID = null;
		/** @type {Promise<any>|null} */
		this.customerPrintSettingsLoad = null;
	}

	onAfterInitialize()
	{
		if (this.options.Wedge && this.options.Wedge.Enabled)
		{
			this.enableWedge(this.options.Wedge);
		}
		if (this.options.CaptureGeolocation)
		{
			this.refreshGeolocation();
		}
		return super.onAfterInitialize();
	}

	// ---- Codes ------------------------------------------------------------

	/** @return {import('headlight-qrcode/source/Headlight-QRCode-Profiles.js').QRProfile} */
	getProfile()
	{
		return this.core.profile;
	}

	/**
	 * Switch customer configuration at runtime (e.g. after login, or in a testbed).
	 *
	 * @param {Object} pConfiguration
	 * @param {Object} [pCustomProperties]
	 * @param {number|string} [pIDCustomer] - Picks the default when nothing is configured.
	 */
	setCustomerConfiguration(pConfiguration, pCustomProperties, pIDCustomer)
	{
		this.core.setCustomerConfiguration(pConfiguration, pCustomProperties, pIDCustomer);
		this.notify('profile', this.core.profile);
	}

	/**
	 * Use a profile resolved elsewhere (e.g. the HeadlightQRCodeHash service's, loaded at sign-in).
	 *
	 * @param {any} pProfile - A resolved profile (headlight-qrcode Profiles.resolveProfile output).
	 */
	useProfile(pProfile)
	{
		if (pProfile && pProfile.CodeFormat)
		{
			this.core.profile = pProfile;
			this.notify('profile', this.core.profile);
		}
	}

	/** @return {string} */
	getEnvironment()
	{
		return this.core.environment;
	}

	/** @param {string} pEnvironment */
	setEnvironment(pEnvironment)
	{
		this.core.environment = pEnvironment;
	}

	decode(pText)
	{
		return this.core.decode(pText);
	}

	encode(pEntity, pValue, pOptions)
	{
		return this.core.encode(pEntity, pValue, pOptions);
	}

	encodeAction(pWorkflow, pVerb, pArguments)
	{
		return this.core.encodeAction(pWorkflow, pVerb, pArguments);
	}

	/**
	 * @param {import('headlight-qrcode/source/Headlight-QRCode-Minter.js').MintContext} pContext
	 * @return {string}
	 */
	mint(pContext)
	{
		return this.core.mint(pContext);
	}

	/** @return {Array<any>} */
	listEntities()
	{
		return this.core.registry.listEntities();
	}

	getEntityDefinition(pEntity)
	{
		return this.core.registry.getEntity(pEntity);
	}

	// ---- Resolution -------------------------------------------------------

	/**
	 * Decode a scan and find the record it points to.
	 *
	 * Live records win over deleted ones. When several live records share a code the most recently
	 * updated is used and the duplicate is reported to operations (a `duplicate` event and a warning
	 * log), never to the person scanning.
	 *
	 * @param {string} pText
	 * @param {Object} [pContext]
	 * @param {Array<string>} [pContext.Accept]
	 * @param {Array<string>} [pContext.RawEntities]
	 * @return {Promise<QRResolution>}
	 */
	async resolve(pText, pContext)
	{
		const tmpContext = pContext || {};
		const tmpDecoded = this.decode(pText);
		if (tmpDecoded.Kind === 'Empty')
		{
			return { Status: 'Empty', Decoded: tmpDecoded };
		}
		if (tmpDecoded.Kind === 'Action')
		{
			return { Status: 'Action', Decoded: tmpDecoded };
		}
		if (tmpDecoded.Entity && tmpContext.Accept && !tmpContext.Accept.includes(tmpDecoded.Entity))
		{
			return { Status: 'Rejected', Decoded: tmpDecoded, Entity: tmpDecoded.Entity, Reason: 'WrongEntity' };
		}
		if (!this.entitySource)
		{
			return { Status: 'Unresolved', Decoded: tmpDecoded, Entity: tmpDecoded.Entity || undefined };
		}

		let tmpCandidates = tmpDecoded.Entity ? [ tmpDecoded.Entity ] : (tmpContext.RawEntities || this.options.DefaultRawEntities);
		if (!tmpDecoded.Entity && tmpContext.Accept)
		{
			tmpCandidates = tmpCandidates.filter((pEntity) => tmpContext.Accept.includes(pEntity));
		}
		const tmpValues = [ tmpDecoded.Value ];
		if (tmpDecoded.Raw !== tmpDecoded.Value)
		{
			tmpValues.push(tmpDecoded.Raw);
		}

		let tmpDeleted = null;
		for (const tmpEntity of tmpCandidates)
		{
			const tmpDefinition = this.core.registry.getEntity(tmpEntity);
			if (!tmpDefinition)
			{
				continue;
			}
			for (const tmpValue of tmpValues)
			{
				const tmpRows = await this.entitySource.find({ Definition: tmpDefinition, Key: tmpDecoded.Key, Value: tmpValue, IncludeDeleted: true });
				const tmpLive = tmpRows.filter((pRow) => !pRow.Deleted);
				if (tmpLive.length > 0)
				{
					tmpLive.sort((pA, pB) => String(pB.UpdateDate || '').localeCompare(String(pA.UpdateDate || '')));
					if (tmpLive.length > 1)
					{
						this.reportDuplicate(tmpDefinition.Entity, tmpValue, tmpLive);
					}
					return { Status: 'Found', Decoded: tmpDecoded, Entity: tmpDefinition.Entity, Record: tmpLive[0], DuplicateCount: tmpLive.length - 1 };
				}
				if (!tmpDeleted && tmpRows.length > 0)
				{
					tmpRows.sort((pA, pB) => String(pB.DeleteDate || '').localeCompare(String(pA.DeleteDate || '')));
					tmpDeleted = { Entity: tmpDefinition.Entity, Record: tmpRows[0] };
				}
			}
		}
		if (tmpDeleted)
		{
			let tmpDeletedBy = null;
			if (typeof this.entitySource.getUserName === 'function')
			{
				tmpDeletedBy = await this.entitySource.getUserName(tmpDeleted.Record.DeletingIDUser);
			}
			return { Status: 'Deleted', Decoded: tmpDecoded, Entity: tmpDeleted.Entity, Record: tmpDeleted.Record, DeletedByName: tmpDeletedBy || undefined };
		}
		return { Status: 'NotFound', Decoded: tmpDecoded, Entity: tmpDecoded.Entity || undefined };
	}

	/**
	 * @param {string} pEntity
	 * @param {string} pValue
	 * @param {Array<Record<string, any>>} pRecords
	 */
	reportDuplicate(pEntity, pValue, pRecords)
	{
		const tmpDefinition = this.core.registry.getEntity(pEntity);
		const tmpIDs = pRecords.map((pRecord) => pRecord[tmpDefinition.IDColumn]);
		this.log.warn(`HeadlightQR: ${pRecords.length} live ${pEntity} records share the code "${pValue}" (IDs ${tmpIDs.join(', ')}); using the most recently updated.`);
		this.notify('duplicate', { Entity: pEntity, Value: pValue, IDs: tmpIDs });
	}

	/**
	 * Display summary of a record for the result sheet: the entity source may supply one.
	 *
	 * @param {string} pEntity
	 * @param {Record<string, any>} pRecord
	 * @return {{ Title: string, Status?: string, Fields: Array<{ Label: string, Value: any }> }}
	 */
	describeRecord(pEntity, pRecord)
	{
		if (this.entitySource && typeof this.entitySource.describe === 'function')
		{
			const tmpSummary = this.entitySource.describe(pEntity, pRecord);
			if (tmpSummary)
			{
				return tmpSummary;
			}
		}
		const tmpDefinition = this.core.registry.getEntity(pEntity) || {};
		const tmpTitle = (pRecord && (pRecord[tmpDefinition.NameColumn] || pRecord[tmpDefinition.Column] || pRecord[tmpDefinition.GUIDColumn])) || 'Untitled';
		const tmpFields = [];
		if (tmpDefinition.Column && pRecord && pRecord[tmpDefinition.Column])
		{
			tmpFields.push({ Label: 'Code', Value: pRecord[tmpDefinition.Column] });
		}
		return { Title: String(tmpTitle), Status: pRecord && pRecord.Status, Fields: tmpFields };
	}

	/**
	 * Where a found record can be opened, from the app's `DestinationResolver` option.
	 *
	 * @param {QRResolution} pResolution
	 * @return {Promise<{ Destinations: Array<{ Label: string, URL?: string, onSelect?: () => void }>, Blocked?: { Reason: string, Message: string } }>}
	 */
	async getDestinations(pResolution)
	{
		if (typeof this.options.DestinationResolver !== 'function')
		{
			return { Destinations: [] };
		}
		const tmpRouting = await this.options.DestinationResolver(pResolution);
		return tmpRouting || { Destinations: [] };
	}

	// ---- Rendering and printing -------------------------------------------

	/**
	 * @param {string} pPayload
	 * @param {Object} [pOptions] - `{ SizePX, Width, Title, ErrorCorrection }`
	 * @return {string} SVG markup
	 */
	renderSVG(pPayload, pOptions)
	{
		const tmpOptions = pOptions || {};
		const tmpMatrix = libMatrix.createMatrix(pPayload, { ErrorCorrection: tmpOptions.ErrorCorrection || this.options.ErrorCorrection });
		return libMatrix.toSVG(tmpMatrix, { Width: tmpOptions.Width || (tmpOptions.SizePX ? `${tmpOptions.SizePX}px` : undefined), Title: tmpOptions.Title });
	}

	/**
	 * @param {string} pPayload
	 * @param {Object} [pOptions]
	 * @return {import('../rendering/QR-Matrix.js').QRMatrix}
	 */
	measure(pPayload, pOptions)
	{
		return libMatrix.createMatrix(pPayload, { ErrorCorrection: (pOptions && pOptions.ErrorCorrection) || this.options.ErrorCorrection });
	}

	/** @return {Array<import('../labels/Label-Media-Presets.js').MediaPreset>} */
	getMediaPresets()
	{
		return libPresets.MEDIA_PRESETS;
	}

	/**
	 * The stock Print uses: the one picked in Print options on this device, else the default.
	 *
	 * @return {import('../labels/Label-Media-Presets.js').MediaPreset}
	 */
	getDeviceMediaPreset()
	{
		let tmpID = null;
		try
		{
			tmpID = window.localStorage.getItem(PRESET_STORAGE_KEY);
		}
		catch (pError)
		{
			tmpID = null;
		}
		return libPresets.getMediaPreset(tmpID) || this.getDefaultMediaPreset();
	}

	/**
	 * The stock for a device that hasn't picked one: the customer's default, else the app's.
	 *
	 * @return {import('../labels/Label-Media-Presets.js').MediaPreset}
	 */
	getDefaultMediaPreset()
	{
		return libPresets.getMediaPreset(this.customerMediaPresetID) || libPresets.getMediaPreset(this.options.DefaultMediaPreset) || libPresets.MEDIA_PRESETS[0];
	}

	/** @return {boolean} true when this device has picked its own stock in Print options */
	hasDeviceMediaPreset()
	{
		try
		{
			return !!libPresets.getMediaPreset(window.localStorage.getItem(PRESET_STORAGE_KEY));
		}
		catch (pError)
		{
			return false;
		}
	}

	/** Go back to the default stock on this device. */
	clearDeviceMediaPreset()
	{
		try
		{
			window.localStorage.removeItem(PRESET_STORAGE_KEY);
		}
		catch (pError)
		{
			// Blocked storage: nothing was remembered.
		}
	}

	/**
	 * @param {Object} [pSettings] - The customer's `QRCodes/Printing` configuration: `{ DefaultMediaPreset }`.
	 */
	setCustomerPrintSettings(pSettings)
	{
		const tmpID = pSettings && pSettings.DefaultMediaPreset;
		this.customerMediaPresetID = libPresets.getMediaPreset(tmpID) ? tmpID : null;
	}

	/**
	 * Load the customer's print settings once. A failed read leaves the app default in place and is retried next time.
	 *
	 * @param {{ getJSON: (pPath: string, fCallback: (pError: any, pResult: any) => void) => void }} pRestClient
	 * @return {Promise<void>}
	 */
	loadCustomerPrintSettings(pRestClient)
	{
		if (!pRestClient || (typeof pRestClient.getJSON !== 'function'))
		{
			return Promise.resolve();
		}
		if (!this.customerPrintSettingsLoad)
		{
			const tmpLoad = new Promise((fResolve) =>
			{
				pRestClient.getJSON('Configuration/For/QRCodes/Printing', (pError, pResult) => fResolve((pError || !pResult || pResult.Error) ? null : pResult));
			}).then((pSettings) =>
			{
				if (pSettings)
				{
					this.setCustomerPrintSettings(pSettings);
				}
				else if (this.customerPrintSettingsLoad === tmpLoad)
				{
					this.customerPrintSettingsLoad = null;
				}
			});
			this.customerPrintSettingsLoad = tmpLoad;
		}
		return this.customerPrintSettingsLoad;
	}

	/** @param {string} pPresetID */
	setDeviceMediaPreset(pPresetID)
	{
		try
		{
			window.localStorage.setItem(PRESET_STORAGE_KEY, pPresetID);
		}
		catch (pError)
		{
			// Private windows and blocked storage: the choice just isn't remembered.
		}
	}

	/**
	 * Labels for entity records: payload from the customer's envelope plus text lines.
	 *
	 * @param {Array<{ Entity: string, Value: string, Lines?: Array<string> }>} pItems
	 * @return {Array<import('../labels/Label-Renderer.js').LabelContent>}
	 */
	buildLabels(pItems)
	{
		return pItems.map((pItem) =>
		{
			if (pItem.Image)
			{
				return { Payload: pItem.Payload || '', Image: pItem.Image, ImageWidth: pItem.ImageWidth, ImageHeight: pItem.ImageHeight };
			}
			return { Payload: pItem.Payload || this.encode(pItem.Entity, pItem.Value), Lines: pItem.Lines || [ pItem.Value ] };
		});
	}

	/**
	 * @param {Array<{ Entity?: string, Value?: string, Payload?: string, Lines?: Array<string> }>} pItems
	 * @param {Object} [pOptions] - `{ PresetID, StartIndex }`
	 * @return {{ HTML: string, PageCSS: string, Plans: Array<any>, Preset: any }}
	 */
	previewLabels(pItems, pOptions)
	{
		const tmpOptions = pOptions || {};
		const tmpPreset = libPresets.getMediaPreset(tmpOptions.PresetID) || this.getDeviceMediaPreset();
		const tmpJob = libLabels.renderPrintJob(tmpPreset, this.buildLabels(pItems), { ErrorCorrection: this.options.ErrorCorrection, MinModuleMM: this.options.MinModuleMM, StartIndex: tmpOptions.StartIndex });
		return Object.assign({ Preset: tmpPreset }, tmpJob);
	}

	/**
	 * Open the print dialog (stock choice + true-size preview); printing happens on its Print click.
	 *
	 * @param {Array<{ Entity?: string, Value?: string, Payload?: string, Lines?: Array<string> }>} pItems
	 * @param {Object} [pOptions] - `{ Title }`
	 */
	openPrintDialog(pItems, pOptions)
	{
		const tmpDialog = this.pict.views['HeadlightQR-PrintDialog'];
		if (!tmpDialog)
		{
			throw new Error('HeadlightQR-PrintDialog view is not registered.');
		}
		tmpDialog.open(pItems, pOptions);
	}

	/**
	 * One label rasterised at the printer's resolution (what native bridges receive).
	 *
	 * @param {{ Entity?: string, Value?: string, Payload?: string, Lines?: Array<string> }} pItem
	 * @param {Object} [pOptions] - `{ PresetID }`
	 * @return {HTMLCanvasElement}
	 */
	renderLabelCanvas(pItem, pOptions)
	{
		const tmpPreset = libPresets.getMediaPreset(pOptions && pOptions.PresetID) || this.getDeviceMediaPreset();
		return libLabels.renderLabelCanvas(tmpPreset, this.buildLabels([ pItem ])[0], { ErrorCorrection: this.options.ErrorCorrection, MinModuleMM: this.options.MinModuleMM });
	}

	/**
	 * Print labels. Must be called from inside a user's click for browser printing.
	 *
	 * @param {Array<{ Entity?: string, Value?: string, Payload?: string, Lines?: Array<string> }>} pItems
	 * @param {Object} [pOptions] - `{ PresetID, StartIndex, Transport: 'auto'|'browser'|'native' }`
	 * @return {Promise<{ Transport: string, Plans: Array<any> }>}
	 */
	async printLabels(pItems, pOptions)
	{
		const tmpOptions = pOptions || {};
		const tmpPreset = libPresets.getMediaPreset(tmpOptions.PresetID) || this.getDeviceMediaPreset();
		const tmpBridge = this.getNativeBridge();
		const tmpUseNative = (tmpOptions.Transport === 'native') || (tmpOptions.Transport !== 'browser' && tmpBridge && tmpBridge.hasNativeSupport());
		if (tmpUseNative && tmpBridge)
		{
			const tmpLabels = this.buildLabels(pItems);
			const tmpImages = tmpLabels.map((pLabel) => pLabel.Image || libLabels.renderLabelCanvas(tmpPreset, pLabel, { ErrorCorrection: this.options.ErrorCorrection, MinModuleMM: this.options.MinModuleMM }).toDataURL('image/png'));
			await libPrint.printNative(tmpBridge, tmpImages, 'HeadLight labels');
			return { Transport: 'native', Plans: [] };
		}
		const tmpJob = this.previewLabels(pItems, Object.assign({}, tmpOptions, { PresetID: tmpPreset.Id }));
		await libPrint.printHTML(tmpJob.HTML, tmpJob.PageCSS);
		return { Transport: 'browser', Plans: tmpJob.Plans };
	}

	/**
	 * @return {any} the app's NativeBridge service/provider when it can print, else null
	 */
	getNativeBridge()
	{
		const tmpBridge = (this.pict.providers && this.pict.providers.NativeBridge) || (this.fable.servicesMap && this.fable.servicesMap.NativeBridge && Object.values(this.fable.servicesMap.NativeBridge)[0]) || null;
		if (tmpBridge && typeof tmpBridge.printImage === 'function' && typeof tmpBridge.hasNativeSupport === 'function')
		{
			return tmpBridge;
		}
		return null;
	}

	// ---- Scanning ---------------------------------------------------------

	/**
	 * @param {import('../scanning/Wedge-Burst-Detector.js').WedgeOptions} [pDetectorOptions]
	 */
	enableWedge(pDetectorOptions)
	{
		if (!this.wedge)
		{
			this.wedge = new WedgeScanSource(
				{
					Detector: pDetectorOptions,
					onScan: (pText, pDetails) => this.handleWedgeScan(pText, pDetails),
				});
		}
		this.wedge.enable();
		this.notify('mode', this.getScanState());
	}

	disableWedge()
	{
		if (this.wedge)
		{
			this.wedge.disable();
		}
		this.notify('mode', this.getScanState());
	}

	/**
	 * Turn the keyboard scanner on while a scan session needs it, in an app that otherwise leaves it off.
	 * @return {() => void} release; the scanner goes back off when the last holder releases, unless the app had it on
	 */
	holdWedge()
	{
		if (!this._wedgeHolds)
		{
			this._wedgeOnBeforeHolds = this.isWedgeEnabled();
			if (!this._wedgeOnBeforeHolds)
			{
				this.enableWedge(this.options.Wedge);
			}
		}
		this._wedgeHolds = (this._wedgeHolds || 0) + 1;
		let tmpReleased = false;
		return () =>
		{
			if (tmpReleased)
			{
				return;
			}
			tmpReleased = true;
			this._wedgeHolds--;
			if (this._wedgeHolds === 0 && !this._wedgeOnBeforeHolds)
			{
				this.disableWedge();
			}
		};
	}

	/** @return {boolean} */
	isWedgeEnabled()
	{
		return !!(this.wedge && this.wedge.enabled);
	}

	/**
	 * @param {string} pText
	 * @param {any} pDetails
	 */
	handleWedgeScan(pText, pDetails)
	{
		if (pDetails && pDetails.Pick && pDetails.PickElement)
		{
			const tmpDecoded = this.decode(pText);
			const tmpElement = /** @type {any} */ (pDetails.PickElement);
			tmpElement.value = (tmpDecoded.Kind === 'Entity') ? tmpDecoded.Value : tmpDecoded.Raw;
			tmpElement.dispatchEvent(new Event('change', { bubbles: true }));
			this.recordScan({ Source: 'wedge', Raw: pText, Mode: 'Pick', Handler: 'focused-field', Status: 'Picked' });
			return;
		}
		this.receiveScan(pText, { Source: 'wedge' });
	}

	/**
	 * Add a scan handler on top of the stack. A GoTo handler goes on top of the other GoTo handlers but stays below
	 * any open Pick or Session, so an app default registered late (scan to open) can't take a cart's scans.
	 *
	 * @param {ScanHandler} pHandler
	 * @return {() => void} removes the handler
	 */
	pushScanHandler(pHandler)
	{
		let tmpIndex = this.handlers.length;
		if (pHandler.Mode === 'GoTo')
		{
			while (tmpIndex > 0 && this.handlers[tmpIndex - 1].Mode !== 'GoTo')
			{
				tmpIndex--;
			}
		}
		this.handlers.splice(tmpIndex, 0, pHandler);
		this.notify('mode', this.getScanState());
		return () =>
		{
			const tmpIndex = this.handlers.indexOf(pHandler);
			if (tmpIndex >= 0)
			{
				this.handlers.splice(tmpIndex, 1);
				this.notify('mode', this.getScanState());
			}
		};
	}

	/**
	 * Register what an action card does when no session owns its workflow, e.g. `cart/open` starts the cart.
	 *
	 * @param {string} pWorkflow
	 * @param {string} pVerb
	 * @param {(pDecoded: any) => void} fEntry
	 */
	registerActionEntry(pWorkflow, pVerb, fEntry)
	{
		this.actionEntries[`${pWorkflow}/${pVerb}`] = fEntry;
	}

	/**
	 * @return {{ Mode: string, Label: string, Handler: string|null, Wedge: boolean }}
	 */
	getScanState()
	{
		const tmpTop = this.handlers[this.handlers.length - 1];
		return {
			Mode: tmpTop ? tmpTop.Mode : 'Idle',
			Label: tmpTop ? (tmpTop.Label || '') : '',
			Handler: tmpTop ? tmpTop.Hash : null,
			Wedge: this.isWedgeEnabled(),
		};
	}

	/**
	 * Feed a scan from any source through decoding, resolution and the handler stack.
	 *
	 * @param {string} pText
	 * @param {{ Source?: 'wedge'|'camera'|'manual'|'native' }} [pOptions]
	 * @return {Promise<ScanEvent>}
	 */
	async receiveScan(pText, pOptions)
	{
		const tmpSource = (pOptions && pOptions.Source) || 'manual';
		const tmpDecoded = this.decode(pText);
		const tmpState = this.getScanState();

		if (tmpDecoded.Kind === 'Action')
		{
			for (let i = this.handlers.length - 1; i >= 0; i--)
			{
				const tmpHandler = this.handlers[i];
				if ((tmpHandler.Workflow && tmpHandler.Workflow === tmpDecoded.Workflow) || (Array.isArray(tmpHandler.Workflows) && tmpHandler.Workflows.includes(tmpDecoded.Workflow)))
				{
					const tmpResolution = { Status: 'Action', Decoded: tmpDecoded };
					const tmpEvent = this.recordScan({ Source: tmpSource, Raw: pText, Mode: tmpHandler.Mode, Handler: tmpHandler.Hash, Status: 'Action', Resolution: tmpResolution });
					await tmpHandler.onScan(tmpResolution, tmpEvent);
					return tmpEvent;
				}
			}
			const tmpEntry = this.actionEntries[`${tmpDecoded.Workflow}/${tmpDecoded.Verb}`];
			if (tmpEntry)
			{
				const tmpEvent = this.recordScan({ Source: tmpSource, Raw: pText, Mode: tmpState.Mode, Handler: 'action-entry', Status: 'Action', Resolution: { Status: 'Action', Decoded: tmpDecoded } });
				tmpEntry(tmpDecoded);
				return tmpEvent;
			}
			const tmpResolution = { Status: 'Rejected', Decoded: tmpDecoded, Reason: 'ActionOutOfContext' };
			const tmpEvent = this.recordScan({ Source: tmpSource, Raw: pText, Mode: tmpState.Mode, Handler: null, Status: 'ActionOutOfContext', Resolution: tmpResolution });
			this.notify('unhandled', tmpEvent);
			return tmpEvent;
		}

		// One lookup per way of reading the code, however many handlers decline it, and one recorded scan (one tone).
		const tmpResolutions = new Map();
		const fResolve = async (pAccept, pRawEntities) =>
		{
			const tmpKey = JSON.stringify([ pAccept || null, pRawEntities || null ]);
			if (!tmpResolutions.has(tmpKey))
			{
				tmpResolutions.set(tmpKey, await this.resolve(pText, { Accept: pAccept, RawEntities: pRawEntities }));
			}
			return tmpResolutions.get(tmpKey);
		};
		for (let i = this.handlers.length - 1; i >= 0; i--)
		{
			const tmpHandler = this.handlers[i];
			const tmpResolution = await fResolve(tmpHandler.Accept, tmpHandler.RawEntities);
			const tmpDraft = { Source: tmpSource, Raw: pText, Mode: tmpHandler.Mode, Handler: tmpHandler.Hash, Status: tmpResolution.Status, Resolution: tmpResolution };
			const tmpHandled = await tmpHandler.onScan(tmpResolution, tmpDraft);
			if (tmpHandled !== false)
			{
				return this.recordScan(tmpDraft);
			}
		}

		const tmpResolution = await fResolve(undefined, undefined);
		const tmpEvent = this.recordScan({ Source: tmpSource, Raw: pText, Mode: 'Idle', Handler: null, Status: tmpResolution.Status, Resolution: tmpResolution });
		this.notify('unhandled', tmpEvent);
		return tmpEvent;
	}

	/**
	 * Open the camera for a single scan in Pick mode.
	 *
	 * @param {Object} [pOptions] - `{ Accept, RawEntities, Label }`
	 * @return {Promise<QRResolution|null>} null when cancelled
	 */
	scanOnce(pOptions)
	{
		const tmpOptions = pOptions || {};
		const tmpCamera = this.pict.views['HeadlightQR-Camera'];
		return new Promise((fResolve) =>
		{
			let tmpSettled = false;
			const fRemove = this.pushScanHandler(
				{
					Hash: 'scan-once',
					Mode: 'Pick',
					Label: tmpOptions.Label || 'Scan a code',
					Accept: tmpOptions.Accept,
					RawEntities: tmpOptions.RawEntities,
					onScan: (pResolution) =>
					{
						if (pResolution.Status === 'Rejected')
						{
							this.notify('feedback', { Status: 'Rejected', Resolution: pResolution });
							return true;
						}
						fFinish(pResolution);
						return true;
					},
				});
			const fFinish = (pResult) =>
			{
				if (tmpSettled)
				{
					return;
				}
				tmpSettled = true;
				fRemove();
				if (tmpCamera)
				{
					tmpCamera.close();
				}
				fResolve(pResult);
			};
			if (tmpCamera)
			{
				tmpCamera.open({ Title: tmpOptions.Label || 'Scan a code', onCancel: () => fFinish(null) });
			}
		});
	}

	/**
	 * @param {Partial<ScanEvent>} pEvent
	 * @return {ScanEvent}
	 */
	recordScan(pEvent)
	{
		this.scanCounter++;
		const tmpEvent = Object.assign({ ID: `scan-${this.scanCounter}`, Time: new Date().toISOString(), Geolocation: this.options.CaptureGeolocation ? this.lastGeolocation : null }, pEvent);
		this.scanLog.unshift(tmpEvent);
		if (this.scanLog.length > SCAN_LOG_LIMIT)
		{
			this.scanLog.pop();
		}
		this.giveFeedback(tmpEvent.Status);
		this.notify('scan', tmpEvent);
		if (this.options.CaptureGeolocation)
		{
			this.refreshGeolocation();
		}
		return tmpEvent;
	}

	/**
	 * Keep a recent location fix so scans can carry one without waiting on the GPS. Never asks for permission.
	 * @return {Promise<void>}
	 */
	refreshGeolocation()
	{
		return libScanGeolocation.getPositionIfPermitted().then((pPosition) =>
		{
			if (pPosition)
			{
				this.lastGeolocation = { Latitude: pPosition.Latitude, Longitude: pPosition.Longitude, AccuracyMeters: pPosition.Accuracy, FixTime: new Date().toISOString() };
			}
		});
	}

	/**
	 * A short tone and vibration so a scanner user knows the scan landed without looking.
	 *
	 * @param {string} pStatus
	 */
	giveFeedback(pStatus)
	{
		const tmpGood = [ 'Found', 'Action', 'Picked' ].includes(pStatus);
		const tmpFeedback = this.options.Feedback || {};
		if (tmpFeedback.Vibrate && typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function')
		{
			navigator.vibrate(tmpGood ? 40 : [ 60, 60, 60 ]);
		}
		if (tmpFeedback.Sound && typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext))
		{
			try
			{
				this.audioContext = this.audioContext || new (window.AudioContext || window.webkitAudioContext)();
				const tmpOscillator = this.audioContext.createOscillator();
				const tmpGain = this.audioContext.createGain();
				tmpOscillator.frequency.value = tmpGood ? 1320 : 330;
				tmpGain.gain.value = 0.05;
				tmpOscillator.connect(tmpGain);
				tmpGain.connect(this.audioContext.destination);
				tmpOscillator.start();
				tmpOscillator.stop(this.audioContext.currentTime + (tmpGood ? 0.08 : 0.25));
			}
			catch (pError)
			{
				// Audio is optional feedback.
			}
		}
	}

	// ---- Events -----------------------------------------------------------

	/**
	 * @param {(pType: string, pDetail: any) => void} fListener - Types: scan, mode, unhandled, duplicate, feedback, profile.
	 * @return {() => void} unsubscribe
	 */
	subscribe(fListener)
	{
		this.listeners.push(fListener);
		return () =>
		{
			this.listeners = this.listeners.filter((pListener) => pListener !== fListener);
		};
	}

	/**
	 * @param {string} pType
	 * @param {any} pDetail
	 */
	notify(pType, pDetail)
	{
		for (const tmpListener of this.listeners.slice())
		{
			try
			{
				tmpListener(pType, pDetail);
			}
			catch (pError)
			{
				this.log.error(`HeadlightQR listener failed on ${pType}: ${pError.message || pError}`);
			}
		}
	}
}

module.exports = HeadlightQRProvider;
module.exports.default_configuration = _DefaultConfiguration;
